import classNames from 'classnames';
import { FC } from 'react';
import { PublicProviderServiceDataFragment } from '../../../generated/graphql';
import ServiceCard from '../ServiceCard';
import { useAuth } from '../../../contexts/AuthContext';
import { parseMarkdown } from '../../lib/markdown';

export const DEFAULT_TAGLINE =
  'Tagline that encapsulates the support you offer';
export const DEFAULT_ABOUT =
  "Provide a succinct yet compelling narrative of your professional journey, highlighting your credentials, philosophy, and what sets you apart. Focus on why you're passionate about your niche and how your work transforms lives.";

const PractitionerInfoRightColumn: FC<{
  tagline?: string;
  about?: string;
  inEditMode?: boolean;
  editModeTaglineChildren?: React.ReactNode;
  editModeAboutChildren?: React.ReactNode;
  publicServices?: PublicProviderServiceDataFragment[];
}> = ({
  tagline,
  about,
  inEditMode,
  editModeTaglineChildren,
  editModeAboutChildren,
  publicServices,
}) => {
  const { authedProviderUser } = useAuth();
  return (
    <div
      className={classNames(
        'flex min-w-0 flex-1 flex-col overflow-hidden',
        'w-full px-4 sm:px-6',
        'space-y-6 sm:space-y-8',
        !publicServices ? 'md:pr-12' : 'md:pr-16',
      )}
    >
      {!inEditMode ? (
        <div
          className={classNames(
            'text-left font-serif text-subtitle font-light text-neutral-125 md:mt-2',
            'md:mt-0 md:text-title-small',
          )}
        >
          {tagline || DEFAULT_TAGLINE}
        </div>
      ) : (
        editModeTaglineChildren
      )}
      <div
        className={classNames(
          'min-w-0 overflow-x-clip',
          !publicServices ? 'md:pr-8' : 'md:pr-16',
        )}
      >
        <div
          className={classNames(
            'mb-3 cursor-default text-body font-medium text-neutral-150',
            inEditMode && ' text-neutral-100',
          )}
        >
          About
        </div>
        {!inEditMode ? (
          <div
            className={classNames(
              'min-w-0 break-words text-caption text-neutral-125',
              'prose prose-sm max-w-none overflow-x-hidden',
              '[&_a]:text-secondary-50 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1',
            )}
            dangerouslySetInnerHTML={{
              __html: parseMarkdown(about || DEFAULT_ABOUT),
            }}
          />
        ) : (
          editModeAboutChildren
        )}
      </div>
      {publicServices?.length > 0 &&
        (() => {
          // Group services while preserving the order from backend (which is ordered by group display_order, then service display_order)
          const groups: Array<{
            groupId: string;
            groupName: string | null;
            services: typeof publicServices;
          }> = [];
          const groupMap = new Map<string, number>(); // Maps groupId to index in groups array

          publicServices.forEach((service) => {
            const groupId = service.serviceGroupId || 'ungrouped';
            const groupName = service.serviceGroupName || null;

            let groupIndex = groupMap.get(groupId);
            if (groupIndex === undefined) {
              // First time seeing this group, add it to groups array
              groupIndex = groups.length;
              groups.push({
                groupId,
                groupName,
                services: [],
              });
              groupMap.set(groupId, groupIndex);
            }

            groups[groupIndex].services.push(service);
          });

          return (
            <div className="min-w-0 space-y-6">
              {groups.map((group) => (
                <div key={group.groupId} className="min-w-0 space-y-4">
                  {group.groupName && (
                    <div className="cursor-default text-body font-medium text-neutral-150">
                      {group.groupName}
                    </div>
                  )}
                  <div className="flex min-w-0 flex-wrap justify-start gap-4 sm:gap-6">
                    {group.services.map((publicService) => (
                      <ServiceCard
                        key={publicService.id}
                        publicService={publicService}
                        providerPrimaryColor={authedProviderUser?.primaryColor}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
    </div>
  );
};

export default PractitionerInfoRightColumn;
